/**
 * I9 woodcut beat: 3 hits per tree on a rhythm (one beat every 0.8 s). An on-beat hit (within 0.15 s of a beat)
 * gives +1 pine log and a 20% chance of resin; an off-beat hit gives nothing. The tree falls after the 3rd hit.
 * The client calls `start()` / `hit()` on `world.sys.chopBeat` and listens to the `chopBeat` events.
 */
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`). */
export type ChopBeatCommand = { t: 'chopStart' } | { t: 'chopHit' };
/** Events (part of `WorldEvent`). */
export type ChopBeatEvent =
  | { type: 'chopBeat'; onBeat: boolean; log: number; resin: number; done: boolean }
  | { type: 'chopBeatStart' };

export const CHOP_BEAT = { period: 0.8, tolerance: 0.15, hits: 3, resinChance: 0.2 } as const;

export class ChopBeatSystem implements WorldSystem {
  readonly name = 'chopBeat';
  active = false;
  hits = 0;
  private t = 0;
  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    chopStart: () => {
      if (!this.active && this.h.world.trades.nodeNear(this.h.world.player)?.kind.terrain === 'tree') this.start();
    },
    chopHit: () => {
      this.hit();
    },
  };

  start(): void {
    this.active = true;
    this.hits = 0;
    this.t = 0;
    this.h.emit({ type: 'chopBeatStart' });
  }

  /** Seconds since the last beat (0..period) -> true when within tolerance of a beat. */
  get onBeat(): boolean {
    const p = this.t % CHOP_BEAT.period;
    return p <= CHOP_BEAT.tolerance || CHOP_BEAT.period - p <= CHOP_BEAT.tolerance;
  }

  hit(): { onBeat: boolean; log: number; resin: number; done: boolean } | null {
    if (!this.active) return null;
    const on = this.onBeat;
    let log = 0;
    let resin = 0;
    if (on) {
      log = this.h.world.trades.give('pine-log', 1);
      if (this.h.random() < CHOP_BEAT.resinChance) resin = this.h.world.trades.give('resin', 1);
    }
    this.hits++;
    const done = this.hits >= CHOP_BEAT.hits;
    if (done) this.active = false;
    this.h.emit({ type: 'chopBeat', onBeat: on, log, resin, done });
    return { onBeat: on, log, resin, done };
  }

  step(dt: number): void {
    if (this.active) this.t += dt;
  }
}
